import { createParams } from 'api/api-utils';
import queryApi from 'api/query';
import { CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { Database } from 'shared/database.enum';
import { CataloguePayload } from '.';
import { AnimalParents } from '../animal/animal-parents';
import { Breeders, Catalogue } from './catalogue';
import { CatalogueAnimals } from './catalogue-animals';
import { CatalogueCount } from './catalogue-count';
import { CatalogueExtended } from './catalogue-extended';
import { CataloguesResponse } from './catalogue-response';
import { CatalogueSort } from './catalogue-sort';
import { CatalogueType } from './catalogue-type.enum';
import { UpdateCatalogueResult } from './update-catalogue-result';

/**
 * Handles all requests to the `/catalogue` resource.
 *
 * Note: Catalogue API does not use front end caching. All requests use a maxAge of `0`.
 */
class CatalogueApi {
  public pageSize = 20;

  private resource = 'catalogue';

  /**
   * Gets a list of sales or semen catalogues.
   */
  getCatalogueList = async (catalogueType: CatalogueType, sort: CatalogueSort, dbType: Database = 2, token?: CancelToken, isCurrent?: boolean | null, pageNumber: number = 0, pageSize: number = this.pageSize) => {
    const { sortBy, sortDir } = sort;
    const response = await queryApi.get(
      `/${this.resource}`,
      {
        params: {
          catalogueType,
          isCurrent,
          pageSize,
          pageNumber,
          sortBy,
          sortDir,
          dbType,
        },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(CataloguesResponse, response.data);
  }

  /**
   * Gets a list of sales or semen catalogues for current user.
   */
  getMyCatalogueList = async (userId: string, catalogueType: CatalogueType, sort: CatalogueSort, dbType: Database, token?: CancelToken) => {
    const { sortBy, sortDir } = sort;

    const response = await queryApi.get(
      `/${this.resource}/my`,
      {
        params: {
          userId,
          catalogueType,
          sortBy,
          sortDir,
          dbType,
        },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(Catalogue, response.data as []);
  }

  /**
   * Gets a individual catalogue.
   *
   * This does not cache the response as getting by id is needed to edit.
   */
  getById = async (id: number | string, db: Database, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/${id}`,
      {
        params: {
          dbType: db,
        },
        cache: {
          maxAge: 0,
        },
        cancelToken: token,
      },
    );
    return plainToClass(CatalogueExtended, response.data);
  }

  /**
   * Creates a new catalogue.
   */
  create = async (catalogue: CataloguePayload, userId?: string, token?: CancelToken) => {
    if (userId) {
      // eslint-disable-next-line no-param-reassign
      catalogue.userId = parseInt(userId);
    }
    const response = await queryApi.post(
      `/${this.resource}`,
      catalogue,
      {
        cancelToken: token,
      },
    );

    return plainToClass(UpdateCatalogueResult, response.data);
  }

  /**
   * Updates an existing catalogue. Requires the payload to have an `id`.
   */
  update = async (catalogue: CataloguePayload, token?: CancelToken) => {
    const response = await queryApi.put(
      `/${this.resource}/${catalogue.id}`,
      catalogue,
      {
        cancelToken: token,
      },
    );

    return plainToClass(UpdateCatalogueResult, response.data);
  }

  /**
   * Delete a catalogue.
   */
  delete = async (id: number, token?: CancelToken) => queryApi.delete(
    `/${this.resource}/${id}`,
    {
      cancelToken: token,
    },
  );

  /**
   * Validates animals provided through the animal listing template .xlsx file.
   */
  validateAnimals = async (data: FormData, token?: CancelToken) => {
    const response = await queryApi.post(
      `/${this.resource}/validateanimals`,
      data,
      {
        cancelToken: token,
      },
    );

    const catalogueAnimals = plainToClass(CatalogueAnimals, response.data);

    if (catalogueAnimals.animals) {
      // Sort animals by lot number ascending.
      catalogueAnimals.animals.sort((a, b) => a.lot - b.lot);
    }

    return catalogueAnimals;
  }

  /**
   * Gets amount of sales and semen catalogues for selected database.
   */
  getCatalogueCount = async (dbType: Database, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/count`,
      {
        params: {
          dbType,
        },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(CatalogueCount, response.data as []);
  }

  /**
   * Gets amount of sales and semen catalogues for selected database for logged in user.
   */
  getMyCatalogueCount = async (dbType: Database, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/my/count`,
      {
        params: {
          dbType,
        },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );
    return plainToClass(CatalogueCount, response.data as []);
  }

  /**
   * Gets breeders by email or username.
   */
  getBreeder = async (keyword?: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/breeders`,
      {
        params: {
          keyword,
        },
        cancelToken: token,
      },
    );

    return plainToClass(Breeders, response.data as any[]);
  }

  /**
   * Gets breeders list for a service provider
   */
  getBreederList = async (token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/breeder-list`,
      {
        cancelToken: token,
      },
    );

    return plainToClass(Breeders, response.data as any[]);
  }

  getAnimalParentsByCatalogue = async (catalogueId: number, dbType: Database, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/parents`,
      {
        params: createParams({
          catalogueId,
          dbType,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(AnimalParents, response.data as []);
  }
}

export default new CatalogueApi();
